import styled from "styled-components";
import { space } from "../../../theming/bca/bca-attributes";
import Box from "../../layout/boxes/Box/Box";

import { StyledDotProps, StyledSwitchContainerProps } from "./Switch.types";

const DOT_SIZES = {
	lg: {
		width: space.md,
		height: space.md,
	},
	md: {
		width: space.sm,
		height: space.sm,
	},
	sm: {
		width: space.xs,
		height: space.xs,
	},
};

const CONTAINER_SIZES = {
	lg: {
		width: space.xxl,
		height: space.lg,
	},
	md: {
		width: space.xl,
		height: space.md,
	},
	sm: {
		width: space.lg,
		height: space.sm,
	},
};

export const StyledDot = styled(
	({ disabled, size, checked, ...props }: StyledDotProps) => <span {...props} />
)`
	${(props) => {
		const dotHeight = DOT_SIZES[props.size]["height"];
		const dotWidth = DOT_SIZES[props.size]["width"];
		const largeDotWidth = `${dotWidth} + (${dotWidth} / 2)`;
		const spacing = props.size === "lg" ? "4px" : "2px";

		const firstBounceValue = `${spacing} * 2 + ${spacing} / 2`;
		const secondBounceValue = `${spacing} * 2 - ${spacing} / 4`;
		const thirdBounceValue = `${spacing} + ${spacing} / 4`;

		const bounceLeftKeyframes = `
  @keyframes bounce-in-left {
  0% {
    left: 4px;
    transform: translateX(0);
    width: calc(${largeDotWidth});
  }
  28% {
    left: calc(100% - ${spacing});
    transform: translateX(-100%);
    width: calc(${dotWidth} - 10%);
  }
  55% {
    left: calc(100% - (${firstBounceValue}));
    transform: translateX(-100%);
    width: ${dotWidth};ß
  }
  72% {
    left: calc(100% - ${spacing});
    transform: translateX(-100%);
    width: calc(${dotWidth} - 7%);
  }
  81% {
    left: calc(100% - (${secondBounceValue}));
    transform: translateX(-100%);
    width: ${dotWidth};
  }
  90% {
    left: calc(100% - ${spacing});
    transform: translateX(-100%);
    width: calc(${dotWidth} - 4%);
  }
  95% {
    left: calc(100% - (${thirdBounceValue}));
    width: ${dotWidth};
    transform: translateX(-100%);
  }
  100% {
    left: calc(100% - ${spacing});
    width: ${dotWidth};
    transform: translateX(-100%);
  }
}
`;

		const bounceRightKeyframes = `
@keyframes bounce-in-right {
  0% {
    left: calc(100% - ${spacing});
    width: calc(${largeDotWidth});
    transform: translateX(-100%);
  }
  28% {
    left: ${spacing};
    transform: translateX(0);
    width: calc(${dotWidth} - 10%);
  }
  55% {
    left: calc(${firstBounceValue});
    transform: translateX(0);
    width: ${dotWidth};
  }
  72% {
    left: ${spacing};
    transform: translateX(0);
    width: calc(${dotWidth} - 7%);
  }
  81% {
    left: calc(${secondBounceValue});
    transform: translateX(0);
    width: ${dotWidth};
  }
  90% {
    left: ${spacing};
    transform: translateX(0);
    width: calc(${dotWidth} - 4%);
  }
  95% {
    left: calc(${spacing});
    transform: translateX(0);
    width: ${dotWidth};
  }
  100% {
    left: ${spacing};
    transform: translateX(0);
    width: ${dotWidth};
  }
}
`;

		const checkedDotColor = props.disabled
			? props.theme.palette.hexForRole("cta_disabled")
			: props.theme.palette.hexForRole("bg_white");

		const uncheckedDotColor = props.disabled
			? props.theme.palette.hexForMandatory("neutral_400")
			: props.theme.palette.hexForRole("bg_white");

		const dotColor = props.checked ? checkedDotColor : uncheckedDotColor;

		const styles = `
  width: ${dotWidth};
  height: ${dotHeight};
	left: ${props.checked ? `calc(100% - ${spacing})` : spacing};
	top: ${spacing};
  border-radius: 20px;
	position: absolute;
	background-color: ${dotColor};
	transition: width 300ms ease-out;
	cursor: pointer;
	user-select: none;
	pointer-events: none;
	transform: ${props.checked ? "translateX(-100%)" : "translateX(0)"};
  transition: width 300ms ease-out;


  &.Switch--bounce-left {
		animation: bounce-in-left 800ms;
		left: calc(100% - ${spacing});
		transform: translateX(-100%);
	}

  &.Switch--bounce-right {
		animation: bounce-in-right 800ms;
	}

`;
		return bounceLeftKeyframes.concat(bounceRightKeyframes, styles);
	}}
`;

export const StyledSwitchContainer = styled(
	({ disabled, ...props }: StyledSwitchContainerProps) => <Box {...props} />
)`
	width: ${(props) => CONTAINER_SIZES[props.size]["width"]};
	height: ${(props) => CONTAINER_SIZES[props.size]["height"]};
	position: relative;
	border-radius: 29px;
	transition: background-color 300ms ease-out;

	&:hover {
		span {
			width: ${(props) =>
				!props.disabled
					? `calc(
				${DOT_SIZES[props.size]["width"]} + (${DOT_SIZES[props.size]["width"]} / 2)
			)`
					: ""};
		}
	}

	${(props) => {
		const checkedBgColor = props.disabled
			? props.theme.palette.hexForRole("cta_disabled")
			: props.theme.palette.hexForMandatory("primary_1_200");
		const uncheckedBgColor = props.disabled
			? props.theme.palette.hexForMandatory("neutral_400")
			: props.theme.palette.hexForMandatory("primary_accent_600");

		return !props.checked
			? `background-color: ${checkedBgColor}`
			: `background-color: ${uncheckedBgColor};`;
	}}
`;

export const StyledInput = styled.input`
	width: 100%;
	height: 100%;
	margin: 0;
	padding: 0;
	appearance: none;
	border-radius: 29px;
	position: absolute;
	top: 0;
	left: 0;
`;
